/**
 * useShiftsData — Laden/CRUD für Shifts, Maßnahmen, Activities.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { toast } from 'sonner';
import { shiftsAPI, employeesAPI, locationsAPI, massnahmenAPI, roomsAPI, activitiesAPI, vacationsAPI, absencesAPI } from '../../lib/api';

export function useShiftsData({ weekStart, selectedLocationId, viewMode, monthDate, autoLoadShifts = true }) {
  const [shifts, setShifts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [massnahmen, setMassnahmen] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const initialLoadDone = useRef(false);

  const loadShifts = useCallback(async () => {
    // Bei jedem Re-Load nach dem ersten: nur Refresh-Flag setzen, damit das
    // Grid weiter sichtbar bleibt → keine DOM-Erneuerung → Scroll-Position bleibt.
    if (initialLoadDone.current) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      let start, end;
      if (viewMode === 'month') {
        start = format(startOfMonth(monthDate), 'yyyy-MM-dd');
        end = format(endOfMonth(monthDate), 'yyyy-MM-dd');
      } else {
        start = format(weekStart, 'yyyy-MM-dd');
        end = format(endOfWeek(weekStart, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      }
      const params = { start_date: start, end_date: end };
      if (selectedLocationId && selectedLocationId !== 'all') {
        params.location_id = selectedLocationId;
      }
      // Urlaube/Abwesenheiten nur für den sichtbaren Zeitraum laden (Overlap),
      // statt alle Datensätze zu holen → deutlich schneller bei großer DB.
      const overlapParams = { overlap_start: start, overlap_end: end };
      const [shiftsRes, activitiesRes, vacationsRes, absencesRes] = await Promise.all([
        shiftsAPI.getAll(params),
        activitiesAPI.getAll(params),
        vacationsAPI.getAll(overlapParams).catch(() => ({ data: [] })),
        absencesAPI.getAll(overlapParams).catch(() => ({ data: [] })),
      ]);
      setShifts(shiftsRes.data);
      setActivities(activitiesRes.data);
      setVacations(vacationsRes.data || []);
      setAbsences(absencesRes.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Einsätze', { id: 'shifts-load-error' });
    } finally {
      setLoading(false);
      setRefreshing(false);
      initialLoadDone.current = true;
    }
  }, [weekStart, selectedLocationId, viewMode, monthDate]);

  const loadMassnahmenForLocation = useCallback(async () => {
    try {
      const params = {};
      if (selectedLocationId && selectedLocationId !== 'all') {
        params.location_id = selectedLocationId;
      }
      const res = await massnahmenAPI.getAll(params);
      setMassnahmen(res.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Maßnahmen', { id: 'massnahmen-load-error' });
    }
  }, [selectedLocationId]);

  const loadBaseData = useCallback(async () => {
    try {
      const [empRes, locRes, roomRes] = await Promise.all([
        employeesAPI.getAll({ is_active: true, taetigkeitsart: 'operativ' }),
        locationsAPI.getAll(),
        roomsAPI.getAll(),
      ]);
      setEmployees(empRes.data);
      setLocations(locRes.data);
      setRooms(roomRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten', { id: 'basedata-load-error' });
    }
  }, []);

  useEffect(() => { loadBaseData(); }, [loadBaseData]);
  useEffect(() => { if (autoLoadShifts) loadShifts(); }, [loadShifts, autoLoadShifts]);
  useEffect(() => { loadMassnahmenForLocation(); }, [loadMassnahmenForLocation]);

  return {
    shifts, activities, employees, locations, rooms, massnahmen, setMassnahmen,
    vacations, absences,
    loading, refreshing,
    loadShifts,
    loadMassnahmen: loadMassnahmenForLocation,
    loadBaseData,
  };
}
